/**
 * Customer + shipping-method preference bank for packing-slip defaults.
 * Collection: packingSlipPrintPreferences
 * Doc id: {customerKey}__{methodKey}
 *
 * Operator: paste Firestore rules in Console (Hosting does not deploy rules).
 */

import {
  doc,
  getDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  buildPackingSlipPreferenceDocId,
  getOrderQuantityTotal,
  getOrderShippingMethodLabel,
  getOrderWeightTotalKg,
  getPackingSlipPreferenceCustomerKey,
  normalizeShippingMethodKey,
  PACKING_SLIP_FORMAT,
  preferenceDocIdForOrder,
} from '../utils/packingSlipPrintDefaults';
import { getOrderPrintSourceId } from './packingSlipPrintCountService';

export const PACKING_SLIP_PRINT_PREFERENCES_COLLECTION = 'packingSlipPrintPreferences';
export const PACKING_SLIP_PREFERENCE_SAMPLE_LIMIT = 20;

export {
  buildPackingSlipPreferenceDocId,
  preferenceDocIdForOrder,
};

/**
 * @param {object[]} orders
 * @returns {Promise<Map<string, object>>}
 */
export async function loadPackingSlipPrintPreferencesForOrders(orders) {
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  const docIds = Array.from(
    new Set(list.map((order) => preferenceDocIdForOrder(order)).filter(Boolean)),
  );
  const out = new Map();
  await Promise.all(
    docIds.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, PACKING_SLIP_PRINT_PREFERENCES_COLLECTION, id));
        if (snap.exists()) out.set(id, snap.data() || {});
      } catch (error) {
        console.warn('[packingSlipPrintPreferences] read failed:', error?.code || error);
      }
    }),
  );
  return out;
}

/**
 * @param {object[]} orders
 * @param {{
 *   format?: string,
 *   jobType?: string,
 *   copies?: number,
 *   copiesByOrder?: Record<string, number>,
 *   userId?: string|null,
 * }} [opts]
 */
export async function recordPackingSlipPrintPreferences(orders, opts = {}) {
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  if (!list.length) return;

  const formatFromJob =
    opts.format === PACKING_SLIP_FORMAT.GO4 ||
    opts.jobType === 'go4' ||
    opts.jobType === 'go4_outside'
      ? PACKING_SLIP_FORMAT.GO4
      : opts.format === PACKING_SLIP_FORMAT.CHEETAH ||
          opts.jobType === 'cheetah_lz17' ||
          opts.jobType === 'cheetah'
        ? PACKING_SLIP_FORMAT.CHEETAH
        : PACKING_SLIP_FORMAT.STANDARD;

  const byDoc = new Map();
  list.forEach((order) => {
    const docId = preferenceDocIdForOrder(order);
    if (!docId) return;
    const sourceId = getOrderPrintSourceId(order);
    const copies = Math.max(
      1,
      Math.min(
        99,
        parseInt(opts.copiesByOrder?.[sourceId], 10) ||
          parseInt(opts.copies, 10) ||
          1,
      ),
    );
    const sample = {
      format: formatFromJob,
      copies,
      qtyTotal: getOrderQuantityTotal(order),
      weightTotal: getOrderWeightTotalKg(order),
      at: Timestamp.now(),
      sourceId: sourceId || null,
    };
    if (!byDoc.has(docId)) {
      byDoc.set(docId, {
        customerKey: getPackingSlipPreferenceCustomerKey(order),
        methodKey: normalizeShippingMethodKey(getOrderShippingMethodLabel(order)),
        methodLabel: getOrderShippingMethodLabel(order),
        samples: [sample],
        lastFormat: formatFromJob,
        lastCopies: copies,
      });
    } else {
      const entry = byDoc.get(docId);
      entry.samples.push(sample);
      entry.lastFormat = formatFromJob;
      entry.lastCopies = copies;
    }
  });

  if (!byDoc.size) return;

  const batch = writeBatch(db);
  let ops = 0;
  for (const [docId, entry] of byDoc.entries()) {
    const ref = doc(db, PACKING_SLIP_PRINT_PREFERENCES_COLLECTION, docId);
    let previousSamples = [];
    try {
      const existing = await getDoc(ref);
      if (existing.exists()) {
        previousSamples = Array.isArray(existing.data()?.samples)
          ? existing.data().samples
          : [];
      }
    } catch {
      previousSamples = [];
    }
    const mergedSamples = [...previousSamples, ...entry.samples].slice(
      -PACKING_SLIP_PREFERENCE_SAMPLE_LIMIT,
    );
    batch.set(
      ref,
      {
        customerKey: entry.customerKey,
        methodKey: entry.methodKey,
        methodLabel: entry.methodLabel,
        lastFormat: entry.lastFormat,
        lastCopies: entry.lastCopies,
        samples: mergedSamples,
        updatedAt: Timestamp.now(),
        ...(opts.userId ? { lastPrintedBy: opts.userId } : {}),
      },
      { merge: true },
    );
    ops += 1;
  }
  if (ops > 0) await batch.commit();
}
